import { useUiStore } from '@/stores/ui.store';
import { confirmDiscardChanges } from '@/utils/unsavedChanges';
import { f7 } from 'framework7-react';

const isSettingsPath = (url: string) => url === '/settings' || url.startsWith('/settings/');

/**
 * Switch the shared main view to a TOP-LEVEL section (Reservations, Orders,
 * Settings, Menu, …). Section switches are an INSTANT screen swap, not a
 * hierarchical push: a slide implies "drill in / swipe back", the wrong
 * affordance between peer sections. Drill-ins WITHIN a section (a settings
 * sub-page, a reservation detail) use a normal `link=`/`router.navigate`.
 *
 * Settings is the exception: on DESKTOP it isn't a section in the main view at
 * all - it's a full-screen popup OVERLAY (see App.tsx) that slides up over the
 * whole app and reveals it intact on dismiss. So "navigating to settings" just
 * opens that popup (the main view stays put underneath), and navigating to any
 * other section closes it and swaps the main view. On MOBILE settings is a tab,
 * so it behaves like any other view.
 */
/**
 * Router of the view that HOSTS a top-level section's pages (Menu, etc.): the
 * single main view on desktop, the active tab's view on mobile - the same view
 * `navigateMain` targets. Use for drill-in push / back WITHIN a section (a
 * category's product page, etc.); `navigateMain` does the section-level SWAP.
 *
 * A hardcoded `views.main` broke mobile (the section lives in a separate tab
 * view there); a hardcoded `views.current` broke desktop (the active view may
 * be the nested settings-detail view). This picks the right one per layout.
 */
export function sectionRouter() {
  const isDesktop = window.innerWidth >= 768;
  const view = isDesktop ? f7.views.main : (f7.views.current ?? f7.views.main);
  // A mutation's onSuccess can fire after the hosting view is torn down (account
  // switch / reloadAll / page unmount mid-mutation), leaving `views.main`
  // undefined. Return null so callers no-op instead of crashing on `.router`.
  return view?.router ?? null;
}

// opts.animate (mobile only): peer-section switches (bottom tab bar) stay an
// instant swap - the default. But opening Menu / Customers / Promotions FROM
// Settings is a drill-in (it gets a Back button), so those call sites pass
// { animate: true } to slide in like every other settings sub-page.
export function navigateMain(path: string, opts?: { animate?: boolean }) {
  // Guard unsaved settings edits first: if a form is dirty, confirm discarding
  // before leaving. `confirmDiscardChanges` resolves immediately (true) when
  // nothing is dirty, so the common path is synchronous-feeling.
  void confirmDiscardChanges().then((proceed) => {
    if (!proceed) return;
    const isDesktop = window.innerWidth >= 768;

    if (isDesktop) {
      const ui = useUiStore.getState();
      // Open the Settings overlay; the main view is untouched so closing it
      // returns to exactly where the user was.
      if (isSettingsPath(path)) {
        ui.setSettingsOpen(true);
        return;
      }
      // Any other section: dismiss the overlay (if open) and swap the single main
      // view. We must use `views.main`, never `views.current`, since the active
      // view may be the nested settings detail view.
      if (ui.settingsOpen) ui.setSettingsOpen(false);
      const view = f7.views.main;
      if (view && view.router.currentRoute?.url !== path) {
        view.router.navigate(path, { animate: false });
      }
      window.dispatchEvent(new CustomEvent('main-navigate', { detail: path }));
      return;
    }

    // Mobile: each tab is its own view stack; navigate the active one. Slide in
    // only when the caller asks (a settings drill-in); peer switches stay instant.
    const view = f7.views.current ?? f7.views.main;
    if (!view) return;
    view.router.navigate(path, { animate: opts?.animate ?? false });
    window.dispatchEvent(new CustomEvent('main-navigate', { detail: path }));
  });
}

// Which mobile tab-view hosts each top-level section. A deep-link (push tap)
// MUST land in the owning tab so the pushed page's Back returns to that
// section's list — not whatever tab happened to be active.
const DEEPLINK_VIEW_BY_PREFIX: readonly [string, string][] = [
  ['/orders', '#view-orders'],
  ['/reservations', '#view-reservations'],
  ['/reservation', '#view-reservations'],
  ['/tables', '#view-tables'],
  ['/settings', '#view-settings'],
];

function deepLinkViewId(path: string): string | null {
  const hit = DEEPLINK_VIEW_BY_PREFIX.find(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`),
  );
  return hit ? hit[1] : null;
}

/**
 * Navigate to a push / notification deep-link target, landing in the section
 * that owns it so the opened page's Back returns to that section's list.
 *
 * On MOBILE each tab is its own view stack. A bare `navigateMain` pushes the
 * deep-link onto whatever tab is currently active (usually Reservations), so
 * `/orders/:id` ends up on the Reservations stack and Back either lands on the
 * wrong section or — on a cold push tap with no history — has nowhere to go and
 * strands the user on a "not found" dead-end. Here we switch to the owning tab
 * first, then push onto ITS router, giving Back the section list to return to.
 * On DESKTOP there is a single main view, so defer to `navigateMain`.
 */
export function navigateDeepLink(path: string) {
  const isDesktop = window.innerWidth >= 768;
  if (isDesktop) {
    navigateMain(path);
    return;
  }
  const viewId = deepLinkViewId(path);
  const view = viewId ? (f7.views.get(viewId) ?? f7.views.current) : f7.views.current;
  if (!view) return;
  if (viewId) f7.tab?.show?.(viewId);
  view.router.navigate(path, { animate: false });
  window.dispatchEvent(new CustomEvent('main-navigate', { detail: path }));
}
